import { formatDate, formatMoney } from '@betterment/core';
import { Box, Button, H2, H6, Separator, Sheet, Text, ToggleTip } from '@betterment/samba-react';

import { NetDeposits } from './NetDeposits';
import { TotalEarned } from './TotalEarned';

import { UserBalancePerformanceQuery } from '../graphql/userPerformanceBalance.generated';

type BalanceDetailsSheetProps = {
  startDate: Date | undefined;
  endDate: Date | undefined;
  data: UserBalancePerformanceQuery['performance']['totals'] | undefined;
};

export const BalanceDetailsSheet: React.FC<BalanceDetailsSheetProps> = ({ data, startDate, endDate }) => {
  if (!data || !startDate || !endDate) {
    return null;
  }

  const { startingBalance, endingBalance, userTransactions, investing, interest, fees, earned } = data;

  return (
    <Sheet overlay="weak">
      <Sheet.Trigger data-samba-component={true} asChild={true}>
        <Button variant="textPrimary" aria-label="See balance details">
          See balance details
        </Button>
      </Sheet.Trigger>
      <Sheet.Content
        css={{
          padding: 'space.400',
          width: '100%',
          maxWidth: 'container.large',
          maxHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <Sheet.Title asChild={true}>
          <H2 variant="title300">Balance details</H2>
        </Sheet.Title>
        <Sheet.Description asChild={true}>
          <Box css={{ display: 'grid', gap: 'space.400', mt: 'space.400' }}>
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ display: 'grid' }}>
                  <Box css={{ display: 'flex' }}>
                    <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                      Starting balance
                    </H6>
                  </Box>
                  <Text variant="text50" color="text.weak">
                    {formatDate(startDate, 'longOrdinalShortMonth')}
                  </Text>
                </Box>
                <Box>
                  <Text>{formatMoney(startingBalance)}</Text>
                </Box>
              </Box>
              <Separator css={{ mt: 'space.400' }} />
            </Box>
            <NetDeposits
              netDeposits={userTransactions.netDeposits}
              deposits={userTransactions.deposits}
              pendingDeposits={userTransactions.pending?.depositsAndTransfersIn}
              withdrawals={userTransactions.withdrawals}
              pendingWithdrawals={userTransactions.pending?.withdrawalsAndTransfersOut}
            />
            <TotalEarned
              totalEarned={earned}
              marketChanges={investing?.marketChanges}
              dividendsReinvested={investing?.dividends?.reinvested}
              dividendsPending={investing?.dividends?.pending}
              interestPaid={interest?.paid}
              managementFees={fees?.management}
            />
            {investing?.adjustments && investing.adjustments.cents !== 0 && (
              <Box>
                <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box css={{ display: 'grid' }}>
                    <Box css={{ display: 'flex' }}>
                      <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                        Adjustments
                      </H6>
                      <ToggleTip>
                        <ToggleTip.Trigger iconName="InfoCircle" label="Toggle Tip" />
                        <ToggleTip.Content side="right" css={{ maxWidth: '300px' }}>
                          Adjustments are manual debits or credits to an account. Details for adjustments are provided
                          on the activity page.
                        </ToggleTip.Content>
                      </ToggleTip>
                    </Box>
                  </Box>
                  <Box>
                    <Text>{formatMoney(investing.adjustments)}</Text>
                  </Box>
                </Box>
                <Separator css={{ mt: 'space.400' }} />
              </Box>
            )}
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ display: 'grid' }}>
                  <Box css={{ display: 'flex' }}>
                    <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                      Ending balance
                    </H6>
                  </Box>
                  <Text variant="text50" color="text.weak">
                    {formatDate(endDate, 'longOrdinalShortMonth')}
                  </Text>
                </Box>
                <Box>
                  <Text>{formatMoney(endingBalance)}</Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Sheet.Description>
      </Sheet.Content>
    </Sheet>
  );
};
